/**
 * The customer display, on its own site (display.<store domain>): a tablet or screen facing the
 * customer, linked to one till with a 6-digit code. It has no till sign-in and no till pages, so
 * a customer can't reach the till from it. Everything goes through the server with the
 * display's own token: the till's screen arrives on a live stream (/display/stream), the
 * customer's taps go back with /display/reply.
 */
import { API_BASE, till } from "./api";

export type PayView =
  /** Customer may tap Mobile Money / Card / Cash. */
  | { stage: "choose"; left: number }
  /** Customer types their MoMo number. */
  | { stage: "number"; left: number }
  /** Paystack sent a request to their phone. */
  | { stage: "prompt"; phone: string | null; provider: string | null; left: number }
  /** Paystack asked for a code (SMS / voucher). */
  | { stage: "otp"; message: string | null }
  /** Scan to pay on their own phone (card or MoMo, in Paystack). */
  | { stage: "qr"; url: string; method: "momo" | "card"; left: number; code?: string | null }
  /** A payment link was texted. */
  | { stage: "link"; phone: string; left: number }
  | { stage: "cash"; left: number }
  | { stage: "declined"; message: string | null };

export type DisplayMessage =
  | { type: "cart"; lines: { name: string; qty: number; total: number }[]; subtotal: number; savings: number; total: number; customer?: string | null }
  | { type: "pay"; view: PayView; customer?: string | null; total?: number }
  | { type: "paid"; total: number; change: number; number: string; customer?: string | null; paidWith?: string | null; warranty?: string | null }
  | { type: "idle" };

export type CustomerMessage =
  | { type: "hello" }
  | { type: "pick-method"; method: "momo" | "card" | "cash" }
  | { type: "momo-number"; phone: string; provider: "mtn" | "vod" | "atl" }
  | { type: "otp"; otp: string }
  /** Customer wants a QR to pay on their own phone instead of typing here. */
  | { type: "scan"; method: "momo" | "card" }
  | { type: "back" }
  | { type: "receipt"; channel: "sms" | "whatsapp" | "paper" | "none" }
  | { type: "rating"; stars: number };

const KEY = "display.link";
export type DisplayLink = { token: string; till: { name: string; number: number } | null };

export function displayLink(): DisplayLink | null {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null") as DisplayLink | null;
  } catch {
    return null;
  }
}
export function saveDisplayLink(l: DisplayLink | null) {
  try {
    if (l) localStorage.setItem(KEY, JSON.stringify(l));
    else localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * A display that was linked while it ran on the till's site arrives with its link in the
 * address (#link=<token>&till=<name>): keep it and clear the address, so it stays linked without
 * typing the code again.
 */
export function takeLinkFromAddress() {
  const h = new URLSearchParams(window.location.hash.slice(1));
  const token = h.get("link");
  if (!token) return;
  const name = h.get("till");
  saveDisplayLink({ token, till: name ? { name, number: Number(h.get("n")) || 0 } : null });
  history.replaceState(null, "", window.location.pathname);
}

/**
 * Opened by the till itself (a second window on the same machine): hand the pairing code to the
 * till so it links this window without anyone typing it. The code only lets a till link this
 * display, so it's safe to send to whichever window opened us.
 */
export function offerCodeToOpener(code: string) {
  try {
    window.opener?.postMessage({ type: "till-display-code", code }, "*");
  } catch {
    /* no opener */
  }
}

/** Listen to the till. `onUnlinked` fires if the server no longer knows this display (unpaired). */
export function listenAsDisplay(f: (m: DisplayMessage) => void, onUnlinked: () => void): () => void {
  const link = displayLink();
  if (!link || typeof EventSource === "undefined") return () => {};
  const url = `${API_BASE}${till("/display/stream")}?token=${encodeURIComponent(link.token)}`;
  let stream: EventSource | null = new EventSource(url);
  stream.addEventListener("screen", (e) => {
    try {
      f(JSON.parse((e as MessageEvent).data) as DisplayMessage);
    } catch {
      /* ignore */
    }
  });
  stream.onerror = () => {
    // 401 closes the stream for good: the till unpaired it. Network blips reconnect by themselves.
    if (stream?.readyState === EventSource.CLOSED) {
      void fetch(url, { method: "GET" })
        .then((r) => {
          if (r.status === 401) {
            saveDisplayLink(null);
            onUnlinked();
          }
        })
        .catch(() => {});
    }
  };
  return () => {
    stream?.close();
    stream = null;
  };
}

/** Customer → till. */
export function replyFromDisplay(msg: CustomerMessage) {
  const link = displayLink();
  if (!link) return;
  void fetch(API_BASE + till("/display/reply"), { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${link.token}` }, body: JSON.stringify({ msg }), credentials: "omit" }).catch(() => {});
}

/** Self-checkout calls to the server, as this display (its own token, never a till's). */
export async function displayApi<T>(path: string, body?: unknown): Promise<T> {
  const token = displayLink()?.token;
  const res = await fetch(API_BASE + till(path), {
    method: body === undefined ? "GET" : "POST",
    headers: { Accept: "application/json", ...(body === undefined ? {} : { "Content-Type": "application/json" }), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: "omit",
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}
